import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { GenericModelList, SumRowsType } from 'drf-react-by-schema';

import Box from '@mui/material/Box';

import { Layout } from 'src/styles';
import { getPerfilLabel, Perfil } from 'src/constants/Perfil';
import { useAppSelector } from 'src/app/hooks';

import PerfilCard from 'src/components/PerfilCard';
import PerfilNoResultsCard from 'src/components/PerfilNoResultsCard';
import { FilterChip, ListHolder } from 'src/styles/styledComponents';
import { GridEnrichedColDef } from '@mui/x-data-grid/models/colDef/gridColDef';
import { getDecimal } from 'src/utils';
import { getProductionKeyLabel, ProductionKey } from 'src/constants';
import ProductionChip from 'src/components/ProductionChip';

const MODEL = 'perfil';

export default function PerfilList() {
  const { usuaria, key, isMobile } = useAppSelector((state) => state.page);
  const [URLSearchParams] = useSearchParams();
  const tipoFromURL = URLSearchParams.get('tipo') as Perfil | null;
  const navigate = useNavigate();
  const [checkedPerfil, setCheckedPerfil] = useState<Perfil>();
  const indexField = 'nome';
  const columnFields = useMemo(() => {
    const newColumnFields = [
      'id',
      'foto',
      'nome',
      'apelido',
      'tipo',
      'grupo',
      'comunidade',
      'is_associated_asmubip',
      'is_coordinator_grupo',
      'is_coordinator_asmubip',
    ];
    if (!checkedPerfil || [Perfil.QUEBRADEIRA, Perfil.DIRECAO].includes(checkedPerfil)) {
      newColumnFields.push(
        ...['azeite_production_total', 'oleo_production_total', 'flocos_production_total']
      );
    }
    newColumnFields.push('ultima_atualizacao');
    return newColumnFields;
  }, [checkedPerfil]);
  const hiddenFields = ['id'];
  const minWidthFields = {
    tipo: 170,
    grupo: 250,
    comunidade: 250,
    azeite_production_total: 140,
    oleo_production_total: 140,
    flocos_production_total: 140,
  };
  const sumRows: SumRowsType | undefined = useMemo(() => {
    if (!checkedPerfil || [Perfil.QUEBRADEIRA, Perfil.DIRECAO].includes(checkedPerfil)) {
      return {
        rows: [
          {
            field: 'azeite_production_total',
            suffix: 'litros',
          },
          // {
          //   field: 'azeite_production_count',
          //   suffix: 'lotes',
          //   isCount: true,
          // },
          {
            field: 'oleo_production_total',
            suffix: 'litros',
          },
          {
            field: 'flocos_production_total',
            suffix: 'Kg',
          },
        ],
        severity: 'info',
      };
    }
    return;
  }, [checkedPerfil]);

  const customColumnOperations: (column: GridEnrichedColDef) => GridEnrichedColDef = useCallback(
    (column) => {
      if (
        ['azeite_production_total', 'oleo_production_total', 'flocos_production_total'].includes(
          column.field
        ) &&
        (!checkedPerfil || [Perfil.QUEBRADEIRA, Perfil.DIRECAO].includes(checkedPerfil))
      ) {
        const path = column.field.replace('_total', '');
        const key = path.replace('_production', '') as ProductionKey;
        column.renderCell = (params) => {
          if (!params.value) {
            return null;
          }
          return (
            <ProductionChip
              type={key}
              label={getDecimal(params.value, getProductionKeyLabel({ key, unit: true }))}
              onClick={() => {
                navigate(`/${path}/?perfil=${params.row.id}`);
              }}
            />
          );
        };
      }
      return column;
    },
    [checkedPerfil]
  );

  const handleClickFilter = (newCheckedPerfil: Perfil) => {
    setCheckedPerfil(newCheckedPerfil === checkedPerfil ? undefined : newCheckedPerfil);
  };

  useEffect(() => {
    if (tipoFromURL) {
      setTimeout(() => {
        setCheckedPerfil(tipoFromURL);
      }, 500);
    }
  }, [tipoFromURL]);

  if (!usuaria) {
    return null;
  }

  return (
    <ListHolder isMobile={isMobile}>
      <Box sx={Layout.topBarOverListWithScrollX}>
        {Object.values(Perfil).map((item) => (
          <FilterChip
            key={`filter_${item}`}
            variant={checkedPerfil === item ? 'filled' : 'outlined'}
            onClick={() => {
              handleClickFilter(item);
            }}
            color="default"
            label={getPerfilLabel({ perfil: item })}
          />
        ))}
      </Box>
      <GenericModelList
        model={MODEL}
        isEditable={false}
        indexField={indexField}
        columnFields={columnFields}
        hiddenFields={hiddenFields}
        minWidthFields={minWidthFields}
        customColumnOperations={customColumnOperations}
        sumRows={sumRows}
        creatableFields={[]}
        paginationMode="server"
        indexFieldBasePath={`/${key}/view/`}
        LinkComponent={Link}
        MobileListRenderItem={PerfilCard}
        MobileRenderNoResults={PerfilNoResultsCard}
        tableAutoHeight={false}
        hasHeader={false}
        queryParams={checkedPerfil ? [`tipo=${checkedPerfil}`] : undefined}
        hideQuickFilterBar={false}
      />
    </ListHolder>
  );
}
